import {Engine, HelperFunctions, IVector2, State} from "../../tsthree";
import {HospitalConfig} from "../Constants/HospitalConfig";
import {PlayerDataSingleton} from "../Logic/PlayerDataSingleton";
import {IMedCaseJSON} from "../Types/IMedCaseJSON";
import {Container, Graphics, Rectangle, Text, TextMetrics, TextStyle} from "pixi.js";
import {GAME_FONT} from "../Constants/Constants";
import {tsthreeConfig} from "../../config/tsthreeConfig";
import {ISymDiagData} from "../Types/ISymDiagData";
import {ProcessState} from "../Constants/ProcessState";
import {ITreatmentData} from "../Types/ITreatmentData";
import {compareArrays} from "../../engine/HelperFunctions/compareArrays";
import {uppercaseFirstLettersInString} from "../../engine/HelperFunctions/uppercaseFirstLettersInString";

type SettingsData = {
    examId: string;
    caseId: string;
    treatmentId: string;
    diagnosisId: string;
};

export type TReqResult = {
    error?: unknown;
    id: string;
    json: object;
    valid?: boolean;
};

export class HospitalMain extends State {

    static idToStringObject: Record<string, string> = null;
    static treatmentsToSymptoms: Record<string, string[]> = null;
    static examsToSymptoms: Record<string, string[]> = null;
    static symptomsToTreatments: Record<string, string[]> = null;
    static symptomsToDiagnoses: Record<string, string[]> = null;
    static diagnosesToSymptoms: Record<string, string[]> = null;
    static diagnosisToExaminations: Record<string, string> = null;

    private _currentCase: IMedCaseJSON;

    constructor() {
        super();
    }

    private static createAvailableExamsList(
        _settings: SettingsData,
        _exams: string[],
        _onChangeToExamDetailPage: Function
    ): {
        mainContainer: Container,
        textObjs: Text[],
    }
    {
        const mainContainer = new Container();
        const textContainer = new Container();
        mainContainer.addChild(textContainer);
        const textObjs: Text[] = [];

        const textStyle = new TextStyle({
            fontFamily: GAME_FONT,
            fill: "red",
            fontSize: 20,
            align: "left"
        });

        const exams = _exams
            .map((e) => ({id: e, str: HospitalMain.idToStringObject[e]}))
            .sort((a, b) => {
                return a.str.localeCompare(b.str);
            });
        exams.forEach((e, i) => {
                const text = new Text(e.str, textStyle);
                textContainer.addChild(text);
                // text.anchor.set(0)
                text.position.set(
                    0,
                    30 * i
                );
                textObjs.push(text);

                // HelperFunctions.makeInteractive(text);
            });
        // textContainer.cacheAsBitmap = true;

        const selectedExam = new Text("-->", textStyle);
        selectedExam.anchor.set(1, 0.5);
        mainContainer.addChild(selectedExam);

        mainContainer.interactive = true;
        mainContainer.interactiveChildren = false;
        mainContainer.buttonMode = true;
        mainContainer.hitArea = new Rectangle(
            0, 0, textContainer.width, textContainer.height
        );

        let pointerOver = false;
        mainContainer.on("pointerover", (ev) => {
            selectedExam.visible = true;
            pointerOver = true;
        });
        mainContainer.on("pointerout", (ev) => {
            selectedExam.visible = false;
            pointerOver = false;
        });
        mainContainer.on("pointermove", (ev) => {
            if(!pointerOver) return;
            const pos = HelperFunctions.parseInteractionEvent(ev);
            pos.x -= (mainContainer.position.x - tsthreeConfig.width);
            pos.y -= (mainContainer.position.y);

            const ind = Math.floor((pos.y - textContainer.y) / 30);

            if(ind >= exams.length) {
                selectedExam.visible = false;
                return;
            } else {
                selectedExam.visible = true;
            }

            selectedExam.position.set(
                mainContainer.width,
                (30 * Math.floor(pos.y / 30)) + 13 + (textContainer.y % 30)
            );
        });
        mainContainer.on("pointerup", (ev) => {
            const pos = HelperFunctions.parseInteractionEvent(ev);
            pos.x -= (mainContainer.position.x - tsthreeConfig.width);
            pos.y -= (mainContainer.position.y);

            const ind = Math.floor((pos.y - textContainer.y) / 30);
            _settings.examId = exams[ind].id;
            mainContainer.parent.position.set(
                -tsthreeConfig.width * 2,
                0
            );
            _onChangeToExamDetailPage();
        });

        const initialY = 0;
        const tMaxY = 0;
        document.addEventListener("wheel", (e) => {
            if(!pointerOver) return;

            const tMinY = initialY - (textContainer.height - 200);
            textContainer.y = Math.min(
                tMaxY,
                Math.max(
                    tMinY,
                    textContainer.y + -e.deltaY
                )
            );

        });
        return {
            mainContainer,
            textObjs
        };
    }

    /**
     * @deprecated
     * @param functionStr
     * @param _params
     * @param isJSON
     */
    public static async makeRequest<T>(
        functionStr: string,
        _params: Record<string, string> = {},
        isJSON: boolean = true
    ): Promise<any> {
        /*
        const authKey = PlayerDataSingleton.getAuthKey();

        let str = `${HospitalConfig.endpoint}${functionStr}?`;
        str += new URLSearchParams(_params).toString();
        try {
            const fetchParams = {
                method: "GET",
                headers: new Headers()
            };
            if(authKey) {
                fetchParams.headers.set(
                    "Authorization",
                    "Bearer " + authKey
                );
            }
            const fetchRes: Response = (
                await fetch(
                    str,
                    fetchParams
                )
                    .catch((err) => {
                        throw err;
                    })
            );
            if(fetchRes.status == 401) {
                console.warn("Not authorized!");
                return null;
            }
            if(isJSON) {
                return (await fetchRes.json()) as T;
            } else {
                return await fetchRes.text() as unknown as T;
            }
        } catch(err) {
            console.warn(err);
        }

        return null;

        */
    }

    public static async getNewCase(): Promise<{id: string; json: IMedCaseJSON}> {
        try {
            const result = await HospitalMain.makeRequest<TReqResult>(
                "requestCase"
            ).then((e) => {
                // console.log(e);
                if(!e.error) {
                    // PlayerDataSingleton.addCase(e.id);
                    return {id: e.id, json: e.json as IMedCaseJSON};
                }
                return null;
            });
            return result;
        } catch(err) {
            console.warn(err);
            return null;
        }
    }

    public static async getCases(): Promise<Array<string>> {
        try {
            return await HospitalMain.makeRequest<{ cases: { assignedCaseIds: string }, error?: unknown; }>(
                "getAssignedCases"
            ).then((e) => {
                // console.log(e);
                if (!e.error) {
                    // console.log(e);
                    return JSON.parse(e.cases.assignedCaseIds);
                } else {
                    console.error(e.error);
                    return null;
                }
            });
        } catch(err) {
            console.error(err);
            return null;
        }
    }

    public static async getCase(caseId: string): Promise<{id: string; json: IMedCaseJSON}> {
        try {
            const result = await HospitalMain.makeRequest<TReqResult>(
                "getCase",
                {
                    caseId: caseId
                }
            ).then((e) => {
                // console.log(e);
                if(!e.error) {
                    return {id: e.id, json: e.json as IMedCaseJSON};
                }
                console.error(e.error);
                return null;
            });
            return result;
        } catch(err) {
            console.error(err);
            return null;
        }
    }

    private static createPatientPage(): {
        nameText: Text,
        symptomsList: Text,
        examsList: Text,
        treatmentsList: Text,
        mainContainer: Container,
        update: (_case: IMedCaseJSON) => void
    }
    {
        let tempCase: IMedCaseJSON = null;

        // mouseover contianer stuff
        const mouseOverContainer = new Container();
        mouseOverContainer.visible = false;
        //
        const mouseOverBg = new Graphics();
        mouseOverBg.lineStyle(2, 0xff00000);
        mouseOverBg.beginFill(0x111111);
        mouseOverBg.drawRect(0, 0, 350, 350);
        mouseOverBg.endFill();
        mouseOverContainer.addChild(mouseOverBg);

        const treatedByText: Text = new Text("Treated by: \n", new TextStyle({
            fill: 0xff0000,
            fontFamily: GAME_FONT,
            fontSize: 22,
            align: "left"
        }));
        treatedByText.position.set(4, 4);
        mouseOverContainer.addChild(treatedByText);
        const indicativeOfText: Text = new Text("Indicative of: \n", new TextStyle({
            fill: 0xff0000,
            fontFamily: GAME_FONT,
            fontSize: 22,
            align: "left"
        }));
        indicativeOfText.position.set(4, 4 + treatedByText.height);
        mouseOverContainer.addChild(indicativeOfText);

        // end mouse ocntianer stuff

        const mainContainer = new Container();
        mainContainer.addChild(mouseOverContainer);

        const fontStyle = new TextStyle({
            fontFamily: "Arial",
            fontSize: 32,
            align: "left",
            fill: "red"
        });

        const patientName = new Text("Name: ", fontStyle);
        mainContainer.addChild(patientName);

        function createSymptomsString(_symptoms: ISymDiagData[]) {
            let symptomsStr = "Symptoms: \n";
            let numberOfHiddenSymptoms = 0;
            _symptoms.forEach((e) => {
                if(e.state == 0) {
                    numberOfHiddenSymptoms++;
                    return;
                }
                symptomsStr += `  - ${HospitalMain.idToStringObject[e.id]} ${
                    ((
                        e.state == ProcessState.SYM_TREATED_VISIBLE ||
                        e.state == ProcessState.SYM_TREATED
                    ) ? "(treated)" : "")
                }\n`;
            });
            symptomsStr += `  - ${numberOfHiddenSymptoms} hidden symptoms`;
            return symptomsStr;
        }

        const symptoms = new Text(createSymptomsString([]), fontStyle);
        symptoms.position.set(
            0,
            60
        );
        mainContainer.addChild(symptoms);

        HelperFunctions.makeInteractive(symptoms, true);
        let lastSelectedElement: number = null;
        let pointerOver = false;
        symptoms.on("pointerover", () => {
            pointerOver = true;
            lastSelectedElement = null;
        });
        symptoms.on("pointerout", () => {
            pointerOver = false;
            lastSelectedElement = null;
            mouseOverContainer.visible = false;
        });
        symptoms.on("pointermove", (ev) => {
            if(!pointerOver) return;
            const pos = HelperFunctions.parseInteractionEvent(ev);
            pos.x -= (mainContainer.position.x) + symptoms.position.x;
            pos.y -= (mainContainer.position.y) + symptoms.position.y;
            mouseOverContainer.position.set(pos.x + symptoms.position.x + 15, pos.y + symptoms.position.y + 15);

            let visibleSymptoms: string[] = [];
            tempCase.currentSymptoms.forEach(
                (e) => (
                    e.state == ProcessState.SYM_VISIBLE ||
                    e.state == ProcessState.SYM_TREATED_VISIBLE
                ) ? visibleSymptoms.push(e.id) : null
            );
            const selectedSymptom = Math.min(Math.max(
                /*0,*/ Math.floor(Math.min(
                symptoms.height, pos.y
            ) / 35) - 1)/*, visibleSymptomsLength-1*/);

            if(lastSelectedElement == selectedSymptom) return;
            lastSelectedElement = selectedSymptom;

            if(
                selectedSymptom > visibleSymptoms.length-1 ||
                selectedSymptom < 0
            ) {
                mouseOverContainer.visible = false;
            } else {
                mouseOverContainer.visible = true;

                let treatmentStr = "Treated by:\n";
                let indicativeOfStr = "Indicative of:\n";
                const symptomId = visibleSymptoms[
                    Math.min(Math.max(selectedSymptom, 0), tempCase.currentSymptoms.length - 1)
                ];
                // indicativeOfText.text = "";

                HospitalMain.symptomsToTreatments[symptomId]
                    .map((e) => HospitalMain.idToStringObject[e])
                    .forEach((e, i) => {
                        if(!e) {
                            console.error("Missing treatment string for id %s", HospitalMain.symptomsToTreatments[symptomId][i]);
                            return;
                        }
                        treatmentStr += `  - ${e}\n`;
                    });
                HospitalMain.symptomsToDiagnoses[symptomId]
                    .map((e) => HospitalMain.idToStringObject[e])
                    .slice(0, 10)
                    .forEach((e, i, arr) => {
                        if(!e) {
                            console.error("Missing diagnosis string for id %s", HospitalMain.symptomsToDiagnoses[symptomId][i]);
                            return;
                        }
                        if(
                            i >= (arr.length - 1) && HospitalMain.symptomsToDiagnoses[symptomId].length > arr.length
                        ) {
                            indicativeOfStr += `  - ${
                                HospitalMain.symptomsToDiagnoses[symptomId].length - arr.length
                            } other diagnoses...\n`;
                        } else {
                            indicativeOfStr += `  - ${e}\n`;
                        }
                    });
                treatedByText.text = treatmentStr;
                treatedByText.updateText(true);
                indicativeOfText.text = indicativeOfStr;
                indicativeOfText.updateText(true);

                mouseOverBg.clear();
                mouseOverBg.lineStyle(2, 0xff00000);
                mouseOverBg.beginFill(0x111111);
                mouseOverBg.drawRect(
                    0, 0,
                    Math.max(treatedByText.width, indicativeOfText.width) + 16,
                    (treatedByText.height - 15) + (indicativeOfText.height - 15)
                );
                mouseOverBg.endFill();
            }
        });

        function createExamsString(_attemptedExaminations: ITreatmentData[]) {
            let examsStr = "Exams: ";
            if(_attemptedExaminations.length == 0) {
                examsStr += "n/a";
            } else {
                examsStr += "\n";
                _attemptedExaminations.forEach((e) => {
                    let stateStr = "";
                    switch(e.state) {
                        case ProcessState.EXAM_IN_PROGRESS:
                            if(e.endTime < Date.now()) {
                                stateStr = "Done?";
                            } else {
                                stateStr = "In Progress";
                            }
                            break;
                        case ProcessState.EXAM_COMPLETE:
                        case ProcessState.EXAM_COMPLETE_ACK:
                            stateStr = "Done";
                            break;
                    }

                    examsStr += `  - ${HospitalMain.idToStringObject[e.id]} (${stateStr})\n`;
                });
            }
            return examsStr;
        }

        const textMetricSymptoms = TextMetrics.measureText(symptoms.text, fontStyle);
        const attemptedExaminations = new Text(createExamsString([]), fontStyle);
        attemptedExaminations.position.set(
            0,
            80 + textMetricSymptoms.height,
        );
        mainContainer.addChild(attemptedExaminations);

        function createTreatmentsString(_treatments: ITreatmentData[]) {
            let examsStr = "Treatments: ";
            if(_treatments.length == 0) {
                examsStr += "n/a";
            } else {
                examsStr += "\n";
                _treatments.forEach((e) => {
                    let stateStr = "";
                    switch(e.state) {
                        case ProcessState.EXAM_IN_PROGRESS:
                            if(e.endTime < Date.now()) {
                                stateStr = "Done?";
                            } else {
                                stateStr = "In Progress";
                            }
                            break;
                        case ProcessState.EXAM_COMPLETE:
                        case ProcessState.EXAM_COMPLETE_ACK:
                            stateStr = "Done";
                            break;
                    }

                    examsStr += `  - ${HospitalMain.idToStringObject[e.id]} (${stateStr})\n`;
                });
            }
            return examsStr;
        }

        const textMetricExaminations = TextMetrics.measureText(attemptedExaminations.text, fontStyle);
        const treatments = new Text(createTreatmentsString([]), fontStyle);
        treatments.position.set(
            0,
            80 + textMetricExaminations.height + textMetricSymptoms.height,
        );
        mainContainer.addChild(treatments);

        mouseOverContainer.parent.removeChild(mouseOverContainer);
        mainContainer.addChild(mouseOverContainer);

        return {
            nameText: patientName,
            symptomsList: symptoms,
            examsList: attemptedExaminations,
            treatmentsList: treatments,
            mainContainer: mainContainer,
            update: (_case) => {
                tempCase = _case;
                symptoms.text = createSymptomsString(tempCase.currentSymptoms);
                attemptedExaminations.text = createExamsString(tempCase.attemptedExaminations);
                treatments.text = createTreatmentsString(tempCase.currentTreatments);
                const textMetricSymptoms = TextMetrics.measureText(symptoms.text, fontStyle);
                const textMetricExaminations = TextMetrics.measureText(attemptedExaminations.text, fontStyle);
                attemptedExaminations.position.set(
                    0,
                    80 + textMetricSymptoms.height,
                );
                treatments.position.set(
                    0,
                    80 + textMetricExaminations.height + textMetricSymptoms.height,
                );
                patientName.text = `Name: ${
                    uppercaseFirstLettersInString(tempCase.patientName)
                }`;
            }
        };
    }

    private static createDiagnosesListPage(
        settings: SettingsData,
        _diagnoses: string[],
        onChangeToDiagnosisDetailPage: Function
    ) : {
        mainContainer: Container,
        textObjs: Text[]
    }
    {
        const diagnoses = _diagnoses
            .map((e) => {
                return {
                    id: e,
                    str: HospitalMain.idToStringObject[e]
                };
            })
            .sort((a, b) => {
                return a.str.localeCompare(b.str);
            });
        const mainContainer = new Container();
        const textContainer = new Container();
        mainContainer.addChild(textContainer);
        const textObjs: Text[] = [];

        const textStyle = new TextStyle({
            fontFamily: GAME_FONT,
            fill: "red",
            fontSize: 20,
            align: "left"
        });

        diagnoses.forEach((e, i) => {
            const text = new Text(e.str, textStyle);
            textContainer.addChild(text);
            // text.anchor.set(0)
            text.position.set(
                0,
                30 * i
            );
            textObjs.push(text);

            // HelperFunctions.makeInteractive(text);
        });

        const selectedDiagnosis = new Text("<--", textStyle);
        selectedDiagnosis.anchor.set(1.25, 0.5);
        mainContainer.addChild(selectedDiagnosis);

        mainContainer.interactive = true;
        mainContainer.interactiveChildren = false;
        mainContainer.buttonMode = true;
        mainContainer.hitArea = new Rectangle(
            0, 0, textContainer.width, textContainer.height
        );

        let pointerOver = false;
        mainContainer.on("pointerover", (ev) => {
            selectedDiagnosis.visible = true;
            pointerOver = true;
        });
        mainContainer.on("pointerout", (ev) => {
            selectedDiagnosis.visible = false;
            pointerOver = false;
        });
        mainContainer.on("pointermove", (ev) => {
            if(!pointerOver) return;
            const pos = HelperFunctions.parseInteractionEvent(ev);
            pos.x -= (mainContainer.position.x - tsthreeConfig.width);
            pos.y -= (mainContainer.position.y);

            const rowHeight = textContainer.height / diagnoses.length;
            const ind = Math.floor((pos.y - textContainer.y) / rowHeight);

            if(ind >= diagnoses.length) {
                selectedDiagnosis.visible = false;
                return;
            } else {
                selectedDiagnosis.visible = true;
            }

            selectedDiagnosis.position.set(
                0,
                (rowHeight * Math.floor(pos.y / rowHeight)) + 13 + (textContainer.y % rowHeight)
            );
            // @ts-ignore
            // mainContainer.hitArea.y = (textContainer.y % 30);
            // console.log(mainContainer.hitArea);
        });
        mainContainer.on("pointerup", (ev) => {
            const pos = HelperFunctions.parseInteractionEvent(ev);
            pos.x -= (mainContainer.position.x - tsthreeConfig.width);
            pos.y -= (mainContainer.position.y);

            const rowHeight = textContainer.height / diagnoses.length;
            const ind = Math.floor((pos.y - textContainer.y) / rowHeight);
            settings.diagnosisId = diagnoses[ind].id;
            // console.log(diagnoses[ind]);
            onChangeToDiagnosisDetailPage();
        });

        const initialY = 0;
        const tMaxY = 0;
        document.addEventListener("wheel", (e) => {
            if(!pointerOver) return;

            const tMinY = initialY - (textContainer.height - 200);
            textContainer.y = Math.min(
                tMaxY,
                Math.max(
                    tMinY,
                    textContainer.y + -e.deltaY
                )
            );

        });

        return {
            mainContainer: mainContainer,
            textObjs: textObjs,
        };
    }

    private static createTreatmentsListPage(
        settings: SettingsData,
        treatments: string[],
        onChangeToTreatmentDetailPage: Function
    ): {
        mainContainer: Container,
        textObjs: Text[]
    }
    {
        const mainContainer = new Container();
        const textContainer = new Container();
        mainContainer.addChild(textContainer);
        const textObjs: Text[] = [];

        const textStyle = new TextStyle({
            fontFamily: GAME_FONT,
            fill: "red",
            fontSize: 20,
            align: "left"
        });

        treatments.forEach((e, i) => {
            const text = new Text(HospitalMain.idToStringObject[e], textStyle);
            textContainer.addChild(text);
            // text.anchor.set(0)
            text.position.set(
                0,
                30 * i
            );
            textObjs.push(text);

            // HelperFunctions.makeInteractive(text);
        });
        // textContainer.cacheAsBitmap = true;

        const selectedTreatment = new Text("-->", textStyle);
        selectedTreatment.anchor.set(1, 0.5);
        mainContainer.addChild(selectedTreatment);

        mainContainer.interactive = true;
        mainContainer.interactiveChildren = false;
        mainContainer.buttonMode = true;
        mainContainer.hitArea = new Rectangle(
            0, 0, textContainer.width, textContainer.height
        );

        let pointerOver = false;
        mainContainer.on("pointerover", (ev) => {
            selectedTreatment.visible = true;
            pointerOver = true;
        });
        mainContainer.on("pointerout", (ev) => {
            selectedTreatment.visible = false;
            pointerOver = false;
        });
        mainContainer.on("pointermove", (ev) => {
            if(!pointerOver) return;
            const pos = HelperFunctions.parseInteractionEvent(ev);
            pos.x -= (mainContainer.position.x - tsthreeConfig.width);
            pos.y -= (mainContainer.position.y);

            const ind = Math.floor((pos.y - textContainer.y) / 30);

            if(ind >= treatments.length) {
                selectedTreatment.visible = false;
                return;
            } else {
                selectedTreatment.visible = true;
            }

            selectedTreatment.position.set(
                mainContainer.width,
                (30 * Math.floor(pos.y / 30)) + 13 + (textContainer.y % 30)
            );
        });
        mainContainer.on("pointerup", (ev) => {
            const pos = HelperFunctions.parseInteractionEvent(ev);
            pos.x -= (mainContainer.position.x - tsthreeConfig.width);
            pos.y -= (mainContainer.position.y);

            const ind = Math.floor((pos.y - textContainer.y) / 30);
            settings.treatmentId = treatments[ind];
            mainContainer.parent.position.set(
                -tsthreeConfig.width * 4,
                0
            );
            onChangeToTreatmentDetailPage();
        });

        const initialY = 0;
        const tMaxY = 0;
        document.addEventListener("wheel", (e) => {
            if(!pointerOver) return;

            const tMinY = initialY - (textContainer.height - 200);
            textContainer.y = Math.min(
                tMaxY,
                Math.max(
                    tMinY,
                    textContainer.y + -e.deltaY
                )
            );

        });
        return {
            mainContainer,
            textObjs
        };
    }

    private static createTreatmentDetailPage(
        settings: SettingsData,
        _onGiveTreatment: (_newCase: IMedCaseJSON) => void
    ): {
        mainContainer: Container;
        treatedSymptomsText: Text;
        update: Function;
    }
    {
        const mainContainer = new Container();

        const fontStyle = new TextStyle({
            fontFamily: GAME_FONT,
            fill: "red",
            align: "center",
            fontSize: 36
        });

        const title = new Text("", fontStyle);
        title.anchor.set(0.5);
        title.position.set(
            tsthreeConfig.width * 0.5,
            tsthreeConfig.height * 0.2
        );
        mainContainer.addChild(title);

        const treatedSymptomsText = new Text(
            "Treated symptoms: \n",
            Object.assign(
                {},
                fontStyle,
                {
                    align: "left",
                }
            )
        );
        treatedSymptomsText.anchor.set(0, 0);
        treatedSymptomsText.position.set(
            tsthreeConfig.width * 0.1,
            tsthreeConfig.height * 0.3
        );
        mainContainer.addChild(treatedSymptomsText);

        const giveTreatmentButton = new Text(">Give treatment<", fontStyle);
        giveTreatmentButton.anchor.set(0.5);
        HelperFunctions.makeInteractive(giveTreatmentButton);
        giveTreatmentButton.position.set(
            tsthreeConfig.width * 0.5,
            tsthreeConfig.height * 0.7
        );
        mainContainer.addChild(giveTreatmentButton);

        let spamPrevent = false;
        giveTreatmentButton.on("pointerup", () => {
            if(spamPrevent) return;
            spamPrevent = true;
            // _onGiveTreatment();
            HospitalMain.giveTreatment(settings.caseId, settings.treatmentId)
                .then((res) => {
                    if(res && !res.error) {
                        console.log("treatment added");
                        spamPrevent = false;
                        _onGiveTreatment(res.json);
                    } else {
                        console.log("failed to add treatment");
                    }
                });
        });

        return {
            mainContainer: mainContainer,
            treatedSymptomsText: treatedSymptomsText,
            update: () => {
                title.text = this.idToStringObject[settings.treatmentId];

                let str = "Treated symptoms: \n";
                HospitalMain.treatmentsToSymptoms[settings.treatmentId].forEach(
                    (value, index, array) => {
                        const baseStr = HospitalMain.idToStringObject[value];
                        if(!baseStr) {
                            console.error("Missing ID %s", value);
                        }
                        str += `  - ${
                            baseStr
                                .replace(" detected", "")
                                .replace(" discovered", "")
                        }\n`;
                    }
                );
                treatedSymptomsText.text = str;
            }
        };
    }

    private static createDiagnosisDetailPage(
        settings: SettingsData,
        _onMakeSuccessfulDiagnosis: () => void,
        _onMakeUnsuccessfulDiagnosis: () => void
    ): {
        mainContainer: Container
        update: Function
    }
    {
        const mainContainer = new Container();

        const fontStyle = new TextStyle({
            fontFamily: GAME_FONT,
            fill: "red",
            align: "center",
            fontSize: 36
        });

        const title = new Text("", fontStyle);
        title.anchor.set(0.5);
        title.position.set(
            tsthreeConfig.width * 0.5,
            tsthreeConfig.height * 0.2
        );
        mainContainer.addChild(title);

        const contents = new Text("",
            Object.assign({}, fontStyle, {
                fontSize: 20,
                align: "left"
            }));
        contents.anchor.set(0, 0);
        contents.position.set(
            tsthreeConfig.width * 0.2,
            tsthreeConfig.height * 0.3
        );
        mainContainer.addChild(contents);

        const definitiveExamination = new Text(
            "Verified with examination:",
            Object.assign({}, fontStyle, {
                fontSize: 20,
                align: "right"
            })
        );
        definitiveExamination.anchor.set(1, 0);
        definitiveExamination.position.set(
            tsthreeConfig.width * 0.8,
            tsthreeConfig.height * 0.3
        );
        mainContainer.addChild(definitiveExamination);

        const makeDiagnosisButton = new Text(">Make diagnosis<", fontStyle);
        makeDiagnosisButton.anchor.set(0.5);
        HelperFunctions.makeInteractive(makeDiagnosisButton);
        makeDiagnosisButton.position.set(
            tsthreeConfig.width * 0.5,
            tsthreeConfig.height * 0.7
        );
        mainContainer.addChild(makeDiagnosisButton);

        let spamPrevent = false;
        makeDiagnosisButton.on("pointerup", () => {
            if(spamPrevent) return;
            spamPrevent = true;
            HospitalMain.makeDiagnosis(settings.caseId, settings.diagnosisId)
                .then((res: {
                    id: string,
                    success: boolean,
                    error?: string,
                }) => {
                    spamPrevent = false;
                    if(res && !res.error) {
                        if(res.success) {
                            _onMakeSuccessfulDiagnosis();
                        } else {
                            _onMakeUnsuccessfulDiagnosis();
                        }
                    } else {
                        console.log("failed to make diagnosis");
                    }
                });
        });

        return {
            mainContainer: mainContainer,
            update: () => {
                title.text = this.idToStringObject[settings.diagnosisId];

                let contentsStr = "Presents with: \n";
                const symptoms = HospitalMain.diagnosesToSymptoms[settings.diagnosisId];
                symptoms.forEach((e) => {
                    contentsStr += `  - ${HospitalMain.idToStringObject[e]}\n`;
                });
                contents.text = contentsStr;

                const idString =
                    HospitalMain.idToStringObject[
                        HospitalMain.diagnosisToExaminations[
                            settings.diagnosisId
                            ]
                        ];
                if(idString) {
                    definitiveExamination.text =
                        `Verified with examination:\n${
                            idString
                        }`;
                } else {
                    definitiveExamination.text = "";
                }
            }
        };
    }

    private static createExamDetailPage(
        settings: SettingsData,
        _onAddExamination: (_newCase: IMedCaseJSON) => void
    ): {
        mainContainer: Container
        update: Function
    }
    {
        const mainContainer = new Container();

        const fontStyle = new TextStyle({
            fontFamily: GAME_FONT,
            fill: "red",
            align: "center",
            fontSize: 36
        });

        const title = new Text("", fontStyle);
        title.anchor.set(0.5);
        title.position.set(
            tsthreeConfig.width * 0.5,
            tsthreeConfig.height * 0.2
        );
        mainContainer.addChild(title);

        const runExamButton = new Text(">Run exam<", fontStyle);
        runExamButton.anchor.set(0.5);
        HelperFunctions.makeInteractive(runExamButton);
        runExamButton.position.set(
            tsthreeConfig.width * 0.5,
            tsthreeConfig.height * 0.7
        );
        mainContainer.addChild(runExamButton);

        let spamPrevent = false;
        runExamButton.on("pointerup", () => {
            if(spamPrevent) return;
            spamPrevent = true;
            HospitalMain.requestExamination(settings.caseId, settings.examId)
                .then((res) => {
                    spamPrevent = false;
                    if(res && !res.error) {
                        console.log("exam added");
                        console.log(res);
                        _onAddExamination(res.json);
                    } else {
                        console.log("failed to add exam");
                    }
                });
        });

        return {
            mainContainer: mainContainer,
            update: () => {
                title.text = this.idToStringObject[settings.examId];
            }
        };
    }

    private static giveTreatment(caseId: string, treatmentId: string): Promise<{
        id: string,
        error?: string,
        json: IMedCaseJSON
    }> {
        return new Promise<{
            id: string,
            error?: string,
            json: IMedCaseJSON
        }>((res, rej) => {
            HospitalMain.makeRequest("giveTreatment", {
                caseId: caseId,
                treatmentId: treatmentId
            })
                .then((response: {
                    id: string,
                    error?: string,
                    json: IMedCaseJSON
                }) => {
                    if(response.error) {
                        console.error(response.error);
                        res(response);
                    } else {
                        res(response);
                    }
                });
        });
    }

    /**
     * @deprecated
     * @param caseId
     * @param examId
     * @private
     */
    private static requestExamination(caseId: string, examId: string): Promise<{
        id: string,
        error?: string,
        json: IMedCaseJSON
    }> {
        return new Promise<{
            id: string,
            error?: string,
            json: IMedCaseJSON
        }>((res, rej) => {
            HospitalMain.makeRequest("addExamination", {
                caseId: caseId,
                examId: examId
            })
                .then((response: {
                    id: string,
                    error?: string,
                    json: IMedCaseJSON
                }) => {
                    if(response.error) {
                        console.error(response.error);
                        res(response);
                    } else {
                        res(response);
                    }
                });
        });
    }

    private static makeDiagnosis(caseId: string, diagnosisId: string): Promise<{
        id: string,
        error?: string,
        success: boolean
    }> {
        return new Promise<{
            id: string,
            error?: string,
            success: boolean
        }>((res, rej) => {
            HospitalMain.makeRequest("makeDiagnosis", {
                caseId: caseId,
                diagnosisId: diagnosisId
            })
                .then((response: {
                    id: string,
                    error?: string,
                    success: boolean
                }) => {
                    if(response.error) {
                        console.error(response.error);
                        res(response);
                    } else {
                        res(response);
                    }
                });
        });
    }

    private static async getUserData(): Promise<{
        id: string;
        displayName: string;
        numOfSolvedCases: number;
    }> {
        const userData: {
            id: string;
            displayName: string;
            numOfSolvedCases: number;
        } = await HospitalMain.makeRequest(
            "getUserData"
        );

        return userData;
    }

    private static createCaseSelectionPage(
        _onSelectCase: (_case: IMedCaseJSON) => void
    ): {
        mainContainer: Container;
        update: (_cases: IMedCaseJSON[]) => void;
    } {
        let _tempCases: IMedCaseJSON[] = [];
        const mainContainer = new Container();
        const retVal = {
            mainContainer: mainContainer,
            update: (_cases: IMedCaseJSON[]) => {
                _tempCases = _cases;

                let casesListStr = "";
                _cases.forEach((e) => {
                    casesListStr += `${
                        uppercaseFirstLettersInString(e.patientName)
                    } - ${e.currentSymptoms.length} symptoms\n`;
                });
                casesList.text = casesListStr;
            }
        };

        const title = new Text("Current cases", new TextStyle({
            fill: "red",
            fontFamily: GAME_FONT,
            fontSize: 54,
            align: "center",
        }));
        title.anchor.set(0.5, 0);
        title.position.set(tsthreeConfig.width * 0.5, 0);
        mainContainer.addChild(title);

        const requestNewCaseButton = new Text("> Request new case <", title.style);
        requestNewCaseButton.anchor.set(0.5);
        requestNewCaseButton.position.set(
            tsthreeConfig.width * 0.5, tsthreeConfig.height * 0.9
        );
        mainContainer.addChild(requestNewCaseButton);
        HelperFunctions.makeInteractive(requestNewCaseButton, false);
        let spamProtect = false;
        requestNewCaseButton.on("pointerup", async () => {
            if(spamProtect) return;
            spamProtect = true;
            const caseData = await HospitalMain.getNewCase();
            if(!caseData) {
                // error
            } else {
                // PlayerDataSingleton.addCase(caseData.id);
                _tempCases.push(caseData.json);
                retVal.update(_tempCases);
                // _onSelectCase(caseData.json);
            }
            spamProtect = false;
        });

        const casesList = new Text(``, new TextStyle({
            fill: "red",
            fontFamily: GAME_FONT,
            fontSize: 32,
            align: "left",
        }));
        casesList.anchor.set(0, 0);
        casesList.position.set(100, tsthreeConfig.height * 0.2);
        mainContainer.addChild(casesList);

        casesList.interactive = true;
        casesList.interactiveChildren = false;
        casesList.buttonMode = true;
        // casesList.hitArea = new Rectangle(
        //     0, 0, casesList.width, casesList.height
        // );

        let pointerOver = false;
        casesList.on("pointerover", (ev) => {
            // selectedExam.visible = true;
            pointerOver = true;
        });
        casesList.on("pointerout", (ev) => {
            // selectedExam.visible = false;
            pointerOver = false;
        });
        casesList.on("pointermove", (ev) => {
            if(!pointerOver) return;
            const pos = HelperFunctions.parseInteractionEvent(ev);
            pos.x -= (casesList.position.x);
            pos.y -= (casesList.position.y);
            // console.log(pos);

            // const ind = Math.floor((pos.y) / 32);

            // console.log(ind);

            // if(ind >= cases.length) {
            //     // selectedExam.visible = false;
            //     return;
            // } else {
            //     // selectedExam.visible = true;
            // }

            // selectedExam.position.set(
            //     mainContainer.width,
            //     (30 * Math.floor(pos.y / 30)) + 13 + (textContainer.y % 30)
            // );
        });
        casesList.on("pointerup", (ev) => {
            if(!pointerOver) return;
            const pos = HelperFunctions.parseInteractionEvent(ev);
            pos.x -= (casesList.position.x);
            pos.y -= (casesList.position.y);

            const ind = Math.min(Math.floor((pos.y) / 32), _tempCases.length - 1);
            // _settings.examId = exams[ind].id;
            // mainContainer.parent.position.set(
            //     -tsthreeConfig.width * 2,
            //     0
            // );
            // _onChangeToExamDetailPage();
            _onSelectCase(_tempCases[ind]);
        });

        const initialY = 0;
        const tMaxY = 0;
        document.addEventListener("wheel", (e) => {
            if(!pointerOver) return;

            const tMinY = initialY - (casesList.height - 200);
            casesList.y = Math.min(
                tMaxY,
                Math.max(
                    tMinY,
                    casesList.y + -e.deltaY
                )
            );

        });

        return retVal;
    }

    async onAwake(_engine: Engine, _params?: unknown): Promise<void> {
        // clean scene from debug stuff
        _engine.getStage().removeChildren(0, _engine.getStage().children.length);
        _engine.getStage().addChild(this.scene.getStage());

        const symptomIds =
            Object.keys(HospitalMain.idToStringObject).filter((e) => {
                return e.indexOf("SYM_") === 0;
            });
        const diagnosisIds =
            Object.keys(HospitalMain.idToStringObject).filter((e) => {
                return (
                    e.indexOf("DIA_") === 0 ||
                    e.indexOf("TRM_") === 0
                );
            });
        const examIds =
            Object.keys(HospitalMain.idToStringObject).filter((e) => {
                return e.indexOf("EXM_") === 0;
            });
        const treatmentIds =
            Object.keys(HospitalMain.idToStringObject).filter((e) => {
                return e.indexOf("TRT_") === 0;
            });

        let updatePatientPage = (_case: IMedCaseJSON) => {};
        const currentCasesPage = HospitalMain.createCaseSelectionPage(
            (_case: IMedCaseJSON) => {
                settings.caseId = _case.id;

                this._currentCase = _case;
                updatePatientPage(this._currentCase);
                this.scene.getStage().position.set(0,0);
            }
        );
        currentCasesPage.mainContainer.position.set(
            0,
            -tsthreeConfig.height
        );
        this.scene.addObject(currentCasesPage.mainContainer);

        let loadingText = new Text("Loading...", new TextStyle({
            fontFamily: "Arial",
            fontSize: 32,
            align: "center",
            fill: "red"
        }));
        loadingText.anchor.set(0.5);
        loadingText.position.set(tsthreeConfig.width * 0.5, -tsthreeConfig.height + (tsthreeConfig.height * 0.5));
        this.scene.addObject(loadingText);

        let userData: unknown = await HospitalMain.getUserData();
        console.log(userData);

        this.scene.getStage().position.set(0, tsthreeConfig.height);

        const currentCasesServer = await HospitalMain.getCases();
        // if(!compareArrays(PlayerDataSingleton.getCases(), currentCasesServer)) {
        //     PlayerDataSingleton.clearCases();
        //     currentCasesServer.forEach((e) => PlayerDataSingleton.addCase(e));
        // }

        if(PlayerDataSingleton.getCases().length == 0) {
            const newCase = await HospitalMain.getNewCase();
            // PlayerDataSingleton.addCase(newCase.id);
            currentCasesPage.update([newCase.json]);
        } else {
            // const cases = [];
            const caseIds = PlayerDataSingleton.getCases();
            for(let i = 0; i < caseIds.length; i++) {
                // cases.push(
                //     (await HospitalMain.getCase(caseIds[i])).json
                // );
            }
            // currentCasesPage.update(cases);
        }

        loadingText.parent.removeChild(loadingText);
        loadingText.destroy(true);
        loadingText = null;

        // let promise: Promise<{ id: string; json: IMedCaseJSON; }>;
        // if(PlayerDataSingleton.getCases().length == 0) {
        //     promise = this.getNewCase();
        // } else {
        //     promise = HospitalMain.makeRequest<TReqResult>(
        //         "validateCase",
        //         {
        //             caseId: currentCases[0]
        //         },
        //     )
        //         .catch((err) => {
        //             return err;
        //         })
        //         .then((res) => {
        //             if(!res || res.error) {
        //                 console.warn(res.error);
        //                 return;
        //             }
        //             console.log(res);
        //             if(res.valid) {
        //                 // great
        //                 console.log("Case %s is valid!", currentCases[0]);
        //                 return HospitalMain.getCase(currentCases[0]);
        //             } else {
        //                 console.warn("Case %s is invalid!", currentCases[0]);
        //                 PlayerDataSingleton.removeCase(currentCases[0]);
        //                 return this.getNewCase();
        //             }
        //         });
        // }
        //
        // const _res = await promise
        //     .catch((err) => {
        //         console.warn(err);
        //         loadingText.text = "Error connecting\nto server";
        //         return null;
        //     });
        //     // .then((_res) => {
        // if(!_res) return;

        // this._currentCase = _res.json;
        const settings: SettingsData = {
            caseId: null,
            examId: null,
            treatmentId: null,
            diagnosisId: null,
        };

        // console.log(this._currentCase);

        // main patient page
        const startPos: IVector2 = {
            x: tsthreeConfig.width * 0.1,
            y: tsthreeConfig.height * 0.1
        };
        const mainPage = HospitalMain.createPatientPage();
        updatePatientPage = (_case) => {
            mainPage.update(_case);
        };
        const examsListRes = HospitalMain.createAvailableExamsList(
            settings,
            examIds,
            () => {
                examDetailPageRes.update();
            }
        );
        const examDetailPageRes = HospitalMain.createExamDetailPage(
            settings,
            (_newCase) => {
                this.scene.getStage().position.set(0, 0);

                this._currentCase = _newCase;
                mainPage.update(this._currentCase);
            }
        );
        const treatmentsListPage = HospitalMain.createTreatmentsListPage(
            settings,
            treatmentIds,
            () => {
                treatmentDetailPageRes.update();
            }
        );
        const diagnosisDetailPageRes = HospitalMain.createDiagnosisDetailPage(
            settings,
            async () => {
                this.scene.getStage().position.set(0, 0);
                const oldCaseId = this._currentCase.id;
                PlayerDataSingleton.removeCase(oldCaseId);

                this._currentCase = (await HospitalMain.getNewCase()).json;
                settings.caseId = this._currentCase.id;
                mainPage.update(this._currentCase);
            },
            () => {
                this.scene.getStage().position.set(0, 0);
                console.log("Wrong!");
            },
        );
        const diagnosesListPage = HospitalMain.createDiagnosesListPage(
            settings,
            diagnosisIds,
            () => {
                this.scene.getStage().position.set(
                    tsthreeConfig.width * 2,
                    0
                );
                diagnosisDetailPageRes.update();
            }
        );
        const treatmentDetailPageRes = HospitalMain.createTreatmentDetailPage(
            settings,
            (_newCase) => {
                this.scene.getStage().position.set(0, 0);
                this._currentCase = _newCase;
                mainPage.update(this._currentCase);
            }
        );

        mainPage.mainContainer.position.set(
            startPos.x,
            startPos.y,
        );
        this.scene.addObject(mainPage.mainContainer);

        // exams list
        examsListRes.mainContainer.position.set(
            tsthreeConfig.width + (tsthreeConfig.width * 0.4),
            0
        );
        this.scene.addObject(examsListRes.mainContainer);
        examDetailPageRes.mainContainer.position.set(
            (tsthreeConfig.width * 2),
            0
        );
        this.scene.addObject(examDetailPageRes.mainContainer);
        treatmentsListPage.mainContainer.position.set(
            (tsthreeConfig.width * 3) + (tsthreeConfig.width * 0.4),
            0
        );
        this.scene.addObject(treatmentsListPage.mainContainer);

        diagnosisDetailPageRes.mainContainer.position.set(
            -(tsthreeConfig.width * 2),
            0
        );
        this.scene.addObject(diagnosisDetailPageRes.mainContainer);

        diagnosesListPage.mainContainer.position.set(
            (-tsthreeConfig.width) + (tsthreeConfig.width * 0.4),
            0
        );
        this.scene.addObject(diagnosesListPage.mainContainer);

        treatmentDetailPageRes.mainContainer.position.set(
            (tsthreeConfig.width * 4),
            0
        );
        this.scene.addObject(treatmentDetailPageRes.mainContainer);


        // const debugFrame = new Graphics();
        // debugFrame.lineStyle(5, 0xff0000);
        // debugFrame.drawRect(
        //     0, 0,
        //     examsListRes.mainContainer.width,
        //     examsListRes.mainContainer.height
        // );
        // debugFrame.position.set(
        //     examsListRes.mainContainer.position.x,
        //     examsListRes.mainContainer.position.y
        // );
        // this.scene.addObject(debugFrame);

        const fontStyle = new TextStyle({
            fontFamily: "Arial",
            fontSize: 32,
            align: "left",
            fill: "red"
        });

        const viewTreatmentsListButton = new Text("View treatments >>", fontStyle);
        viewTreatmentsListButton.anchor.set(1, 1);
        HelperFunctions.makeInteractive(viewTreatmentsListButton);
        viewTreatmentsListButton.on("pointerup", (ev) => {
            this.scene.getStage().position.set(
                -tsthreeConfig.width * 3,
                0
            );
        });
        viewTreatmentsListButton.position.set(
            tsthreeConfig.width * 1,
            tsthreeConfig.height * 1
        );
        this.scene.addObject(viewTreatmentsListButton);

        const viewExamsListButton = new Text("View exams >>", fontStyle);
        viewExamsListButton.anchor.set(1, 0.5);
        HelperFunctions.makeInteractive(viewExamsListButton);
        viewExamsListButton.on("pointerup", (ev) => {
            this.scene.getStage().position.set(
                this.scene.getStage().position.x + -tsthreeConfig.width,
                0
            );
        });
        viewExamsListButton.position.set(
            tsthreeConfig.width * 1,
            (tsthreeConfig.height * 1) - 60
        );
        this.scene.addObject(viewExamsListButton);

        const viewDiagnosesListButton = new Text(
            "<< View diagnoses",
            Object.assign({}, fontStyle, {align: "right"})
        );
        viewDiagnosesListButton.anchor.set(0, 1);
        HelperFunctions.makeInteractive(viewDiagnosesListButton);
        viewDiagnosesListButton.on("pointerup", (ev) => {
            this.scene.getStage().position.set(
                this.scene.getStage().position.x + tsthreeConfig.width,
                0
            );
        });
        viewDiagnosesListButton.position.set(
            0,
            tsthreeConfig.height * 1
        );
        this.scene.addObject(viewDiagnosesListButton);

        const backToCurrentCasesButton = new Text(
            "^^ Back to Cases ^^",
            Object.assign({}, fontStyle, {align: "right"})
        );
        backToCurrentCasesButton.anchor.set(0.5, 0);
        HelperFunctions.makeInteractive(backToCurrentCasesButton);
        backToCurrentCasesButton.on("pointerup", (ev) => {


            this.scene.getStage().position.set(
                0,
                tsthreeConfig.height
            );
        });
        backToCurrentCasesButton.position.set(
            tsthreeConfig.width * 0.5,
            0
        );
        this.scene.addObject(backToCurrentCasesButton);

        const checkExamsButton = new Text(">Check exams<", fontStyle);
        checkExamsButton.anchor.set(0.5, 0.5);
        HelperFunctions.makeInteractive(checkExamsButton);
        let spamProtect_exam: boolean = false;
        checkExamsButton.on("pointerup", (ev) => {
            if(spamProtect_exam) return;
            spamProtect_exam = true;
            this._currentCase.attemptedExaminations.forEach((e) => {
                if(
                    e.state == ProcessState.EXAM_IN_PROGRESS &&
                    e.endTime <= Date.now()
                ) {
                    HospitalMain.makeRequest(
                        "acknowledgeExamination",
                        {
                            caseId: settings.caseId,
                            examId: e.id
                        },
                        true
                    ).then((value: { id: string, json: IMedCaseJSON, error?: boolean }) => {
                        spamProtect_exam = false;
                        if(value.error) {
                            console.error("Error acknowledging exam %s", e.id);
                        } else {
                            this._currentCase.attemptedExaminations = value.json.attemptedExaminations;
                            mainPage.update(value.json);
                        }
                    });
                }
            });
        });
        checkExamsButton.position.set(
            tsthreeConfig.width * 0.5,
            tsthreeConfig.height * 0.8
        );
        this.scene.addObject(checkExamsButton);

        const checkTreatmentsButton = new Text(">Check treatments<", fontStyle);
        checkTreatmentsButton.anchor.set(0.5, 0.5);
        HelperFunctions.makeInteractive(checkTreatmentsButton);
        let spamProtect_treatments: boolean = false;
        checkTreatmentsButton.on("pointerup", (ev) => {
            if(spamProtect_treatments) return;
            spamProtect_treatments = true;
            this._currentCase.currentTreatments.forEach((e) => {
                if(
                    e.state == ProcessState.EXAM_IN_PROGRESS &&
                    e.endTime <= Date.now()
                ) {
                    HospitalMain.makeRequest(
                        "acknowledgeTreatment",
                        {
                            caseId: settings.caseId,
                            treatmentId: e.id
                        },
                        true
                    ).then((value: { id: string, json: IMedCaseJSON, error?: boolean }) => {
                        spamProtect_treatments = false;
                        if(value.error) {
                            console.error("Error acknowledging treatment %s", e.id);
                        } else {
                            this._currentCase.currentTreatments = value.json.currentTreatments;
                            mainPage.update(value.json);
                        }
                    });
                }
            });
        });
        checkTreatmentsButton.position.set(
            tsthreeConfig.width * 0.5,
            tsthreeConfig.height * 0.8 + 50
        );
        this.scene.addObject(checkTreatmentsButton);

        const fromTreatmentsToMainButton = new Text("<< Go back", fontStyle);
        HelperFunctions.makeInteractive(fromTreatmentsToMainButton);
        fromTreatmentsToMainButton.position.set(
            tsthreeConfig.width * 3,
            tsthreeConfig.height * 0.6
        );
        fromTreatmentsToMainButton.on("pointerup", (ev) => {
            this.scene.getStage().position.set(0, 0);
        });
        this.scene.addObject(fromTreatmentsToMainButton);

        const fromTreatmentsToExamsButton = new Text("<< Go back", fontStyle);
        HelperFunctions.makeInteractive(fromTreatmentsToExamsButton);
        fromTreatmentsToExamsButton.position.set(
            tsthreeConfig.width * 4,
            tsthreeConfig.height * 0.6
        );
        fromTreatmentsToExamsButton.on("pointerup", (ev) => {
            this.scene.getStage().position.set(-tsthreeConfig.width * 3, 0);
        });
        this.scene.addObject(fromTreatmentsToExamsButton);

        const fromExamsToMainButton = new Text("<< Go back", fontStyle);
        HelperFunctions.makeInteractive(fromExamsToMainButton);
        fromExamsToMainButton.position.set(
            tsthreeConfig.width,
            tsthreeConfig.height * 0.6
        );
        fromExamsToMainButton.on("pointerup", (ev) => {
            this.scene.getStage().position.set(0, 0);
        });
        this.scene.addObject(fromExamsToMainButton);

        const fromExamDetailToExamsButton = new Text("<< Go back", fontStyle);
        HelperFunctions.makeInteractive(fromExamDetailToExamsButton);
        fromExamDetailToExamsButton.position.set(
            tsthreeConfig.width * 2,
            tsthreeConfig.height * 0.6
        );
        fromExamDetailToExamsButton.on("pointerup", (ev) => {
            this.scene.getStage().position.set(-tsthreeConfig.width, 0);
        });
        this.scene.addObject(fromExamDetailToExamsButton);

        const fromDiagnosesPageToPatientPage = new Text("Go back >>",
            Object.assign({}, fontStyle, {align: "right"})
        );
        fromDiagnosesPageToPatientPage.anchor.set(1, 0.5);
        HelperFunctions.makeInteractive(fromDiagnosesPageToPatientPage);
        fromDiagnosesPageToPatientPage.position.set(
            0,
            tsthreeConfig.height * 0.6
        );
        fromDiagnosesPageToPatientPage.on("pointerup", (ev) => {
            this.scene.getStage().position.set(0, 0);
        });
        this.scene.addObject(fromDiagnosesPageToPatientPage);

        const fromDiagnosesDetailPageToDiagnosesPage = new Text("Go back >>",
            Object.assign({}, fontStyle, {align: "right"})
        );
        fromDiagnosesDetailPageToDiagnosesPage.anchor.set(1, 0.5);
        HelperFunctions.makeInteractive(fromDiagnosesDetailPageToDiagnosesPage);
        fromDiagnosesDetailPageToDiagnosesPage.position.set(
            -tsthreeConfig.width * 1,
            tsthreeConfig.height * 0.6
        );
        fromDiagnosesDetailPageToDiagnosesPage.on("pointerup", (ev) => {
            this.scene.getStage().position.set(tsthreeConfig.width, 0);
        });
        this.scene.addObject(fromDiagnosesDetailPageToDiagnosesPage);

            // });

        _engine.getTicker().start();
    }

    preload(_engine: Engine): Promise<void> {
        const promises: Promise<void>[] = [];

        if(!HospitalMain.idToStringObject) {
            promises.push(new Promise<void>((res, rej) => {
                fetch("./assets/data/idToString.json")
                    .then((data) => {
                        return data.json();
                    })
                    .catch((err) => rej(err))
                    .then((data: Record<string, string>) => {
                        HospitalMain.idToStringObject = data;
                    })
                    .then(() => res());
            }));
        }

        if(!HospitalMain.treatmentsToSymptoms) {
            promises.push(new Promise<void>((res, rej) => {
                fetch("./assets/data/TreatmentsToSymptoms.json")
                    .then((data) => {
                        return data.json();
                    })
                    .catch((err) => rej(err))
                    .then((data: Record<string, string[]>) => {
                        HospitalMain.treatmentsToSymptoms = data;
                    })
                    .then(() => res());
            }));
        }

        if(!HospitalMain.examsToSymptoms) {
            promises.push(new Promise<void>((res, rej) => {
                fetch("./assets/data/ExamsToSymptoms.json")
                    .then((data) => {
                        return data.json();
                    })
                    .catch((err) => rej(err))
                    .then((data: Record<string, string[]>) => {
                        HospitalMain.examsToSymptoms = data;
                    })
                    .then(() => res());
            }));
        }

        if(!HospitalMain.symptomsToTreatments) {
            promises.push(new Promise<void>((res, rej) => {
                fetch("./assets/data/SymptomsToTreatments.json")
                    .then((data) => {
                        return data.json();
                    })
                    .catch((err) => rej(err))
                    .then((data: Record<string, string[]>) => {
                        HospitalMain.symptomsToTreatments = data;
                    })
                    .then(() => res());
            }));
        }

        if(!HospitalMain.symptomsToDiagnoses) {
            promises.push(new Promise<void>((res, rej) => {
                fetch("./assets/data/SymptomsToDiagnoses.json")
                    .then((data) => {
                        return data.json();
                    })
                    .catch((err) => rej(err))
                    .then((data: Record<string, string[]>) => {
                        HospitalMain.symptomsToDiagnoses = data;
                    })
                    .then(() => res());
            }));
        }

        if(!HospitalMain.diagnosesToSymptoms) {
            promises.push(new Promise<void>((res, rej) => {
                fetch("./assets/data/DiagnosesToSymptoms.json")
                    .then((data) => {
                        return data.json();
                    })
                    .catch((err) => rej(err))
                    .then((data: Record<string, string[]>) => {
                        HospitalMain.diagnosesToSymptoms = data;
                    })
                    .then(() => res());
            }));
        }

        if(!HospitalMain.diagnosisToExaminations) {
            promises.push(new Promise<void>((res, rej) => {
                fetch("./assets/data/DiagnosisToExaminations.json")
                    .then((data) => {
                        return data.json();
                    })
                    .catch((err) => rej(err))
                    .then((data: Record<string, string>) => {
                        HospitalMain.diagnosisToExaminations = data;
                    })
                    .then(() => res());
            }));
        }

        return (Promise.allSettled(promises) as unknown as Promise<void>)
            .then(() => {

            });
    }

}
